import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { db, auth } from '@/lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { X, Gift, TrendingUp, CheckCircle, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useToast } from '@/hooks/useToast';
import StoreProductImageCarousel from '@/features/store/components/StoreProductImageCarousel';

const ARTIST_SHARE = 0.7;

const FreeItemModal = ({ product, isOpen, onClose }) => {
  const { user } = useAuth();
  const { format } = useCurrency();
  const { toast } = useToast();
  const [requestEarnings, setRequestEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFlipped, setIsFlipped] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const productPrice = parseFloat(product?.price || 0);
  const threshold = parseFloat(
    product?.free_claim_threshold_gbp ?? product?.price ?? 0
  );

  const loadEarnings = useCallback(async () => {
    if (!user?.uid) return;
    try {
      setLoading(true);
      if (user?.email === 'store@store.com') {
        setRequestEarnings(threshold);
        setLoading(false);
        return;
      }
      const requestsQuery = query(
        collection(db, 'requests'),
        where('artist_id', '==', user.uid)
      );
      const requestsSnapshot = await getDocs(requestsQuery);
      let total = 0;
      requestsSnapshot.docs.forEach((docSnap) => {
        const amount = Number(docSnap.data().amount_paid || 0);
        total += amount * ARTIST_SHARE;
      });
      setRequestEarnings(total);
    } catch (error) {
      console.error('Error loading earnings:', error);
      setRequestEarnings(0);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, user?.email, threshold]);

  useEffect(() => {
    if (isOpen && user) {
      loadEarnings();
      const t = setTimeout(() => setIsFlipped(true), 500);
      return () => clearTimeout(t);
    }
    setIsFlipped(false);
    setConfirmOpen(false);
  }, [isOpen, user, loadEarnings]);

  const progress =
    threshold > 0 ? Math.min((requestEarnings / threshold) * 100, 100) : 0;
  const remaining = Math.max(threshold - requestEarnings, 0);
  const canClaim = threshold > 0 && requestEarnings >= threshold - 0.005;

  const runClaim = async () => {
    if (!user?.uid || !product?.id) return;
    setClaiming(true);
    try {
      const functionsInstance = getFunctions(auth.app, 'us-central1');
      const claimFn = httpsCallable(functionsInstance, 'claimFreeStoreItem');
      await claimFn({ productId: product.id });
      toast({
        title: 'Claim submitted',
        description:
          'Your free item claim is on its way. We will fulfil it from the store.',
      });
      setConfirmOpen(false);
      onClose();
    } catch (err) {
      let msg =
        err?.message ||
        err?.details?.message ||
        'Could not complete claim. Try again or contact support.';
      if (err?.code === 'functions/already-exists') {
        msg = 'You have already claimed this item.';
      }
      toast({
        title: 'Claim failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setClaiming(false);
    }
  };

  if (!isOpen || !product) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100010] flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="relative my-8 w-full max-w-2xl rounded-lg border border-gray-700 bg-gray-800"
          style={{ maxHeight: '90vh' }}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-20 rounded-full bg-gray-700 p-2 text-gray-300 transition-colors hover:bg-gray-600 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>

          <div
            className="relative w-full"
            style={{
              perspective: '1000px',
              minHeight: '500px',
            }}
          >
            <motion.div
              className="relative w-full"
              style={{
                transformStyle: 'preserve-3d',
                minHeight: '500px',
              }}
              animate={{ rotateY: isFlipped ? 180 : 0 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
            >
              <div
                className="absolute inset-0 w-full"
                style={{
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  transform: 'rotateY(0deg)',
                }}
              >
                <div className="flex min-h-[500px] flex-col items-center justify-center p-8 text-center">
                  <div className="mb-6 w-48 max-w-full">
                    <StoreProductImageCarousel
                      product={product}
                      alt={product.name}
                      heightClass="h-48"
                      roundedClassName="rounded-lg"
                    />
                  </div>
                  <h2 className="mb-2 text-3xl font-bold">{product.name}</h2>
                  <p className="mb-4 text-gray-400">{product.description}</p>
                  <p className="mb-6 text-2xl font-bold text-purple-400">
                    {format(productPrice)}
                  </p>
                  <p className="text-sm text-gray-500">Opening your progress…</p>
                </div>
              </div>

              <div
                className="absolute inset-0 w-full"
                style={{
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                }}
              >
                <div
                  className="flex max-h-[90vh] min-h-[500px] flex-col overflow-y-auto p-6"
                >
                  <div className="mb-4 flex items-center justify-center">
                    <Gift className="h-10 w-10 text-green-400" />
                  </div>

                  <h2 className="mb-1 text-center text-xl font-bold">
                    Get <span className="text-purple-400">{product.name}</span>{' '}
                    for FREE!
                  </h2>
                  <p className="mb-6 text-center text-sm text-gray-400">
                    Earn {format(threshold)} in requests, and get this item for FREE!
                  </p>

                  {loading ? (
                    <div className="flex flex-1 items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-500" />
                    </div>
                  ) : (
                    <>
                      <div className="mb-6">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-sm text-gray-400">Progress</span>
                          <span className="text-sm font-bold text-purple-400">
                            {format(requestEarnings)} / {format(threshold)}
                          </span>
                        </div>
                        <div className="relative h-8 w-full overflow-hidden rounded-full bg-gray-700">
                          <motion.div
                            className="relative h-full bg-gradient-to-r from-purple-500 to-pink-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 1, ease: 'easeOut' }}
                          />
                          {progress >= 100 && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <CheckCircle className="h-5 w-5 text-green-400" />
                            </div>
                          )}
                        </div>
                        <div className="mt-2 flex justify-between text-xs text-gray-500">
                          <span>{format(0)}</span>
                          <span>{format(threshold)}</span>
                        </div>
                      </div>

                      {canClaim ? (
                        <div className="mb-6 rounded-lg border border-green-500 bg-green-900/20 p-4">
                          <div className="mb-2 flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-400" />
                            <span className="font-bold text-green-400">
                              You&apos;ve reached the goal!
                            </span>
                          </div>
                          <p className="mb-3 text-sm text-gray-300">
                            You&apos;ve earned {format(requestEarnings)} from requests — enough to
                            claim this item for free.
                          </p>
                          <Button
                            type="button"
                            onClick={() => setConfirmOpen(true)}
                            className="w-full bg-green-600 py-3 font-semibold text-white hover:bg-green-700"
                          >
                            <Sparkles className="mr-2 h-4 w-4" />
                            CLAIM NOW
                          </Button>
                        </div>
                      ) : (
                        <div className="mb-6 rounded-lg border border-purple-500 bg-purple-900/20 p-4">
                          <div className="mb-2 flex items-center space-x-2">
                            <TrendingUp className="h-5 w-5 flex-shrink-0 text-purple-400" />
                            <span className="font-bold text-purple-400">
                              Keep earning and receiving requests!
                            </span>
                          </div>
                          <p className="text-sm text-gray-300">
                            You need to receive {format(remaining)} more in requests to get this
                            item for free.
                          </p>
                        </div>
                      )}

                      <div className="mt-auto border-t border-gray-700 pt-4">
                        <p className="text-xs text-gray-500">
                          Eligible earnings are based on your paid song requests to date. One free
                          claim per item.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="border-gray-600 bg-gray-900 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm free claim</DialogTitle>
            <DialogDescription className="text-gray-400">
              Claim <strong className="text-white">{product.name}</strong> for free? This uses your
              eligible request earnings and can only be done once per item. We&apos;ll fulfil your
              order from the store.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={claiming}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-green-600 hover:bg-green-700"
              onClick={runClaim}
              disabled={claiming}
            >
              {claiming ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                'Confirm claim'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AnimatePresence>
  );
};

export default FreeItemModal;
